/**
 * Registers `public/service-worker.js` — see that file for what it does and, more
 * importantly, for the invariants that keep it out of `shared/lib/app-version`'s way.
 *
 * No React: this runs once from `src/index.tsx`, outside the tree.
 */

const SW_URL = `${process.env.PUBLIC_URL ?? ""}/service-worker.js`;

/**
 * A standalone PWA left open for days does client-side routing only, so the browser
 * never performs the navigation that would trigger its own update check. Without this
 * a shipped fix — including the worker's KILL_SWITCH — could take days to reach a
 * phone. Same floor idea as `useAppUpdate`'s MIN_CHECK_GAP_MS.
 */
const UPDATE_CHECK_GAP_MS = 60 * 60_000;

let registered = false;

/**
 * Whether a service worker may be registered here at all.
 *
 * One natural signal per environment rather than a NODE_ENV branch to keep in sync,
 * following `readCurrentBuildId()`:
 *
 * | environment              | serviceWorker in navigator | isSecureContext | registers |
 * |--------------------------|----------------------------|-----------------|-----------|
 * | jsdom (jest)             | no                         | –               | no        |
 * | react-scripts start      | yes                        | yes (localhost) | no *      |
 * | plain http:// host       | yes                        | NO              | no        |
 * | https://werkhof…de       | yes                        | yes             | YES       |
 *
 * * unless REACT_APP_SW_ON_LOCALHOST=true, which exists so `serve -s build` can be
 *   used to check the PWA before deploying.
 *
 * The insecure-context case is silent on purpose. It is not an error we can fix from
 * here, and it would otherwise log on every page load for anyone on an internal host.
 */
function shouldRegister(): boolean {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return false;
  if (typeof window === "undefined" || !window.isSecureContext) return false;
  const { hostname } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]") {
    return process.env.REACT_APP_SW_ON_LOCALHOST === "true";
  }
  return true;
}

function watchForUpdates(registration: ServiceWorkerRegistration): void {
  let lastCheckedAt = Date.now();
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) return;
    if (Date.now() - lastCheckedAt < UPDATE_CHECK_GAP_MS) return;
    lastCheckedAt = Date.now();
    // A failed check is not actionable — the next one retries by itself.
    void registration.update().catch(() => undefined);
  });
}

/**
 * Runs `task` once the document has finished loading — immediately if that already
 * happened. The readyState branch is not belt-and-braces: `load` is not re-fired for a
 * late listener, so waiting for it unconditionally would mean never registering at all
 * whenever this module runs after the document is complete.
 */
function onceLoaded(task: () => void): void {
  if (document.readyState === "complete") {
    task();
    return;
  }
  window.addEventListener("load", task, { once: true });
}

/**
 * Idempotent. Holds off until the document has loaded so registration never competes
 * with the first paint.
 *
 * A rejected registration is swallowed: a service worker is an enhancement, and a
 * failure here must never take the app down.
 */
export function registerServiceWorker(): void {
  if (registered || !shouldRegister()) return;
  registered = true;

  onceLoaded(() => {
    navigator.serviceWorker
      // `updateViaCache: "none"` keeps the HTTP cache out of the update check, which
      // matters because the company Apache proxy in front of us is not ours to configure.
      .register(SW_URL, { scope: "/", updateViaCache: "none" })
      .then(watchForUpdates)
      .catch(() => undefined);
  });
}
